/**
 * Réception d'e-factures, adossée à Supabase.
 *
 * `en_invoice` et `events` ne sont jamais projetés ici : ce sont les blobs
 * EN16931 complets, réservés à un usage de détail qui n'existe pas encore
 * côté écran. Ce fichier ne lit que la projection dont la liste a besoin.
 */
import type {
  ExecutionContext,
  PurchasesRepository,
  ReceivedInvoice,
  ReceivedInvoiceStatus,
  ReceptionStatus,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const COLUMNS = `
  id, invoice_number, issue_date, payment_due_date, currency_code,
  seller_name, seller_siren, seller_vat, total_ht, total_ttc, total_vat,
  processing_rule, latest_status_code, latest_status_text, status,
  supplier_id, expense_id, created_at
`;

const num = (v: unknown): number | null => (v == null ? null : Number(v));

function toReceivedInvoice(row: Row): ReceivedInvoice {
  return {
    id: row.id,
    invoiceNumber: row.invoice_number ?? null,
    issueDate: row.issue_date ?? null,
    paymentDueDate: row.payment_due_date ?? null,
    currencyCode: row.currency_code ?? null,
    sellerName: row.seller_name ?? null,
    sellerSiren: row.seller_siren ?? null,
    sellerVat: row.seller_vat ?? null,
    totalHt: num(row.total_ht),
    totalTtc: num(row.total_ttc),
    totalVat: num(row.total_vat),
    processingRule: row.processing_rule ?? null,
    latestStatusCode: row.latest_status_code ?? null,
    latestStatusText: row.latest_status_text ?? null,
    status: row.status as ReceivedInvoiceStatus,
    supplierId: row.supplier_id ?? null,
    expenseId: row.expense_id ?? null,
    createdAt: row.created_at,
  };
}

function fail(error: { message: string; code?: string }): never {
  // `already_classified`, `missing_amounts`, `not_found` viennent de
  // `classify_received_invoice` : leur texte est déjà en français et déjà
  // précis, un message générique ferait perdre la seule information utile.
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabasePurchasesRepository(
  clientFor: DbClientFactory,
): PurchasesRepository {
  return {
    async list(ctx: ExecutionContext, status?: ReceivedInvoiceStatus) {
      let query = clientFor(ctx)
        .from("received_invoices")
        .select(COLUMNS)
        // Le plus urgent d'abord : une échéance proche avant une lointaine,
        // et les échéances nulles (facture mal formée côté source) en
        // dernier plutôt qu'en premier.
        .order("payment_due_date", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (status) query = query.eq("status", status);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toReceivedInvoice);
    },

    async classify(ctx: ExecutionContext, receivedInvoiceId, projectId, categoryId) {
      const { data, error } = await clientFor(ctx).rpc("classify_received_invoice", {
        p_received_invoice_id: receivedInvoiceId,
        p_project_id: projectId,
        p_category_id: categoryId,
      });
      if (error) fail(error);
      return String(data);
    },

    async linkSupplier(ctx: ExecutionContext, receivedInvoiceId, supplierId) {
      const { error } = await clientFor(ctx)
        .from("received_invoices")
        .update({ supplier_id: supplierId })
        .eq("id", receivedInvoiceId);
      if (error) fail(error);
    },

    async getReceptionStatus(ctx: ExecutionContext): Promise<ReceptionStatus> {
      const { data, error } = await clientFor(ctx)
        .from("tenants")
        .select(
          "einvoice_pa_provider, einvoice_pa_credentials, einvoice_reception_synced_at, einvoice_reception_error",
        )
        // Pas de `.eq("id", ...)` : la policy limite déjà la lecture au
        // tenant de la session.
        .maybeSingle();
      if (error) fail(error);

      const credentials = (data?.einvoice_pa_credentials ?? null) as Row | null;
      return {
        configured: data?.einvoice_pa_provider === "superpdp" && !!credentials?.client_id,
        lastSyncedAt: data?.einvoice_reception_synced_at ?? null,
        lastError: data?.einvoice_reception_error ?? null,
      };
    },

    async configureReception(ctx: ExecutionContext, clientId: string, clientSecret: string) {
      const { error } = await clientFor(ctx)
        .from("tenants")
        .update({
          einvoice_pa_provider: "superpdp",
          einvoice_pa_api_url: "https://api.superpdp.tech",
          einvoice_pa_credentials: { client_id: clientId, client_secret: clientSecret },
          // Une reconfiguration efface l'erreur précédente : c'est
          // précisément le geste qu'on fait pour la corriger.
          einvoice_reception_error: null,
        })
        // Obligatoire : Supabase refuse tout UPDATE sans clause WHERE
        // explicite, quoi que dise la RLS — trouvé en marchant dessus en
        // direct sur `app.dker.pro`. La policy `tenant_isolation` reste le
        // vrai garde-fou : cet `.eq` ne permet pas de viser un autre tenant.
        .eq("id", ctx.tenantId);
      if (error) fail(error);
    },
  };
}
